import React from "react";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { materialFotoPublicUrl } from "../../api/Materialoteca/MaterialotecaApi";
import FeriaEstadoChip from "./FeriaEstadoChip";
import { materialPrimaryFotoArchivo } from "./feriaStatus";

const ACCENT = "#ffac7e";

/**
 * Pastilla de material (grid) o fila clásica.
 * variant: "pill" | "row"
 */
const FeriaMaterialCard = ({
  row,
  variant = "pill",
  highlighted = false,
  showAssignChip = false,
  assignLabel = "Mover",
  onSelect,
  onEdit,
  onDelete,
  onEstadoChange,
  onAssignClick,
  onThumbClick,
}) => {
  const theme = useTheme();
  const thumb = materialFotoPublicUrl(
    materialPrimaryFotoArchivo(row?.material),
  );
  const name =
    row?.material?.NombreMaterial || `#${row?.id_Materialoteca || "?"}`;
  const specs = [
    row?.material?.Composicion,
    row?.material?.Peso != null && row?.material?.Peso !== ""
      ? `${row.material.Peso} g/m²`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (variant === "pill") {
    return (
      <Box
        id={`feria-material-${row.id_FeriaMaterial}`}
        onClick={() => onSelect?.(row)}
        sx={{
          width: { xs: 140, sm: 156 },
          flex: "0 0 auto",
          borderRadius: 2.25,
          overflow: "hidden",
          bgcolor: highlighted
            ? alpha(ACCENT, 0.16)
            : alpha(theme.palette.text.primary, 0.03),
          border: "1px solid",
          borderColor: highlighted
            ? alpha(ACCENT, 0.55)
            : alpha(theme.palette.text.primary, 0.08),
          boxShadow: `0 1px 3px ${alpha("#0b1a24", 0.05)}`,
          cursor: onSelect ? "pointer" : "default",
          transition: "border-color 0.12s ease, box-shadow 0.12s ease",
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.35),
            boxShadow: `0 4px 12px ${alpha("#0b1a24", 0.08)}`,
          },
        }}
      >
        <Box
          onClick={(e) => {
            if (!thumb) return;
            e.stopPropagation();
            onThumbClick?.(thumb, row);
          }}
          sx={{
            height: 96,
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: thumb ? "zoom-in" : "default",
            position: "relative",
          }}
        >
          {thumb ? (
            <Box
              component="img"
              src={thumb}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageOutlinedIcon sx={{ fontSize: 28, color: "text.disabled" }} />
          )}
          <Stack
            direction="row"
            spacing={0.25}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
            }}
          >
            {onEdit ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row);
                }}
                sx={{
                  bgcolor: alpha("#fff", 0.92),
                  p: 0.35,
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : null}
            {onDelete ? (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row);
                }}
                sx={{
                  bgcolor: alpha("#fff", 0.92),
                  p: 0.35,
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : null}
          </Stack>
        </Box>
        <Box sx={{ p: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.78rem",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "2.5em",
            }}
          >
            {name}
          </Typography>
          {specs ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.35,
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {specs}
            </Typography>
          ) : null}
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.75 }}
          >
            <FeriaEstadoChip
              estado={row.Estado}
              editable={Boolean(onEstadoChange)}
              onChange={(Estado) => onEstadoChange?.(row, Estado)}
            />
            {showAssignChip && onAssignClick ? (
              <Chip
                size="small"
                variant="outlined"
                label={assignLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignClick(e, row);
                }}
                sx={{ fontWeight: 700, height: 22, fontSize: "0.65rem" }}
              />
            ) : null}
          </Stack>
        </Box>
      </Box>
    );
  }

  // variant === "row" (legado)
  return (
    <Box
      id={`feria-material-${row.id_FeriaMaterial}`}
      onClick={() => onSelect?.(row)}
      sx={{
        p: 1.25,
        borderRadius: 2,
        cursor: onSelect ? "pointer" : "default",
        border: "none",
        bgcolor: highlighted
          ? alpha(ACCENT, 0.14)
          : alpha(theme.palette.text.primary, 0.035),
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="flex-start">
        <Box
          onClick={(e) => {
            if (!thumb) return;
            e.stopPropagation();
            onThumbClick?.(thumb, row);
          }}
          sx={{
            width: 56,
            height: 56,
            borderRadius: 1.5,
            flexShrink: 0,
            overflow: "hidden",
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: thumb ? "zoom-in" : "default",
          }}
        >
          {thumb ? (
            <Box
              component="img"
              src={thumb}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageOutlinedIcon sx={{ fontSize: 22, color: "text.disabled" }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.25}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.88rem",
                flex: 1,
                minWidth: 0,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {name}
            </Typography>
            {onEdit ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row);
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            ) : null}
            {onDelete ? (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row);
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            ) : null}
          </Stack>
          {specs ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.2, lineHeight: 1.35 }}
            >
              {specs}
            </Typography>
          ) : null}
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.75 }}
          >
            <FeriaEstadoChip
              estado={row.Estado}
              editable={Boolean(onEstadoChange)}
              onChange={(Estado) => onEstadoChange?.(row, Estado)}
            />
            {showAssignChip && onAssignClick ? (
              <Chip
                size="small"
                variant="outlined"
                label={assignLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignClick(e, row);
                }}
                sx={{ fontWeight: 700 }}
              />
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default FeriaMaterialCard;
